import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/GuesthouseBooking.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const GH_API = API_ROOT.endsWith("/api")
  ? `${API_ROOT}/guesthouse`
  : `${API_ROOT}/api/guesthouse`;

const BOOKING_ALLOWED_EXTS   = ["jpg", "jpeg", "png"];
const BOOKING_MAX_FILE_BYTES = 200 * 1024;

const compressBookingImage = (file, maxBytes) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width  = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      let quality = 0.92;
      const attempt = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Compression failed."));
            if (blob.size <= maxBytes || quality < 0.1) return resolve(blob);
            quality = Math.max(quality - 0.08, 0.05);
            attempt();
          },
          "image/jpeg",
          quality
        );
      };
      attempt();
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Cannot read image.")); };
    img.src = url;
  });

function parseLocalDate(value) {
  if (!value) return null;
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}


const PURPOSE_OPTIONS = [
  { value: "Conference", label: "Conference" },
  { value: "Seminar", label: "Seminar" },
  { value: "Symposia", label: "Symposia" },
  { value: "Selection Committee", label: "Selection Committee" },
  { value: "Lecture", label: "Lecture" },
  { value: "Viva/External Examiner", label: "Viva/External Examiner" },
  { value: "Personal", label: "Personal" },
  { value: "OMeeting", label: "Official Meeting" },
  { value: "Others", label: "Others" }
];

const RELATIONSHIP_OPTIONS = ["Guest", "Relative", "Spouse", "Dependent", "Friend", "Official", "Self"];

export default function GuesthouseBookingRequestAdmin() {
  const navigate = useNavigate();

  const user = (() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const moduleId = parseInt(user?.moduleId, 10);

  const [referenceUsers, setReferenceUsers] = useState([]);
  const [referenceID, setReferenceID] = useState("");
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [guesthouses, setGuesthouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    visitorName: "",
    visitorMobile: "",
    visitorEmail: "",
    address: "",
    stateID: "",
    cityID: "",
    guestHouseID: "",
    roomType: "R",
    visitType: "P",
    purposeOfVisit: "",
    fromDate: "",
    toDate: "",
    noOfDays: 1,
    noOfRooms: 1,
    relationWithApplicant: "Guest",
    remarks: "",
    supportDoc: null,
    departmentID: "",
    employeeID: "",
    ghMemberID: "",
    teacherID: ""
  });

  const [additionalVisitors, setAdditionalVisitors] = useState([]);

  const getOrdinal = (n) => {
    if (n === 1) return "1st";
    if (n === 2) return "2nd";
    if (n === 3) return "3rd";
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  };

  useEffect(() => {
    if (moduleId !== 10) {
      navigate("/dashboard", { replace: true });
    }
  }, [moduleId, navigate]);

  useEffect(() => {
    if (moduleId !== 10) return;

    const load = async () => {
      try {
        const [refRes, statesRes, ghRes] = await Promise.all([
          fetch(`${GH_API}/booking/admin-reference-users?moduleId=${moduleId}`),
          fetch(`${API_ROOT}/api/states`),
          fetch(`${GH_API}/options?moduleId=${moduleId}`)
        ]);
        const refData = await refRes.json();
        const statesData = await statesRes.json();
        const ghData = await ghRes.json();
        setReferenceUsers(Array.isArray(refData) ? refData : []);
        setStates(Array.isArray(statesData) ? statesData : []);
        setGuesthouses(Array.isArray(ghData) ? ghData : []);
      } catch (err) {
        console.error("Failed to load form lookups:", err);
      }
    };
    load();
  }, [moduleId]);

  useEffect(() => {
    if (!formData.stateID) {
      setCities([]);
      return;
    }
    const fetchCities = async () => {
      try {
        const res = await fetch(`${API_ROOT}/api/cities?stateId=${formData.stateID}`);
        const data = await res.json();
        setCities(Array.isArray(data) ? data : []);
      } catch (err) {
        setCities([]);
        console.error("Failed to fetch cities:", err);
      }
    };
    fetchCities();
  }, [formData.stateID]);

  const handleReferenceChange = async (e) => {
    const refId = e.target.value;
    setReferenceID(refId);
    if (!refId) return;

    const prefix = refId.charAt(0);
    const id = refId.slice(1);
    let param = "";
    if (prefix === "D") param = `departmentID=${id}`;
    else if (prefix === "E") param = `employeeID=${id}`;
    else if (prefix === "F") param = `ghmemberID=${id}`;
    else if (prefix === "T") param = `teacherID=${id}`;
    else {
      window.alert("Invalid Reference ID prefix.");
      return;
    }

    try {
      const res = await fetch(`${GH_API}/booking/admin-vip-details?moduleId=${moduleId}&${param}`);
      const data = await res.json();

      if (!data.found) {
        window.alert("Record not found.");
        setFormData(prev => ({ ...prev, visitorMobile: "", visitorEmail: "", address: "", cityID: "" }));
        return;
      }

      setFormData(prev => ({
        ...prev,
        visitorMobile: data.mobile || "",
        visitorEmail: data.email || "",
        address: data.address || "",
        cityID: data.cityID ? String(data.cityID) : "",
        stateID: data.stateID ? String(data.stateID) : prev.stateID,
        departmentID: data.departmentID ? String(data.departmentID) : "",
        employeeID: data.employeeID ? String(data.employeeID) : "",
        ghMemberID: data.ghMemberID ? String(data.ghMemberID) : "",
        teacherID: data.teacherID ? String(data.teacherID) : ""
      }));
    } catch (err) {
      console.error("Failed to fetch reference details:", err);
      window.alert("Failed to fetch reference details.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGuestHouseChange = (e) => {
    const ghID = parseInt(e.target.value, 10);
    const defaultRoomType = ghID === 2 ? "C" : "R";
    setFormData(prev => ({ ...prev, guestHouseID: ghID, roomType: defaultRoomType }));
  };

  const getRoomTypeOptions = () => {
    const options = [{ value: "R", label: "Room" }];
    if (formData.guestHouseID === 1) {
      options.push({ value: "S", label: "Suite" });
    } else if (formData.guestHouseID === 2) {
      options.push({ value: "C", label: "Conference Hall" });
      options.push({ value: "D", label: "Dining Hall" });
      options.push({ value: "E", label: "Executive Dining Hall" });
      options.push({ value: "A", label: "Conference+Dining+Executive Dining Hall" });
    } else if (formData.guestHouseID === 4) {
      options.push({ value: "V", label: "VC Suite" });
    } else if (formData.guestHouseID === 6) {
      options.push({ value: "H", label: "Seminar Hall" });
    }
    return options;
  };

  const handleDateChange = (field, value) => {
    if (!value) {
      setFormData(prev => ({ ...prev, [field]: "", noOfDays: 1 }));
      setFieldErrors(prev => ({ ...prev, [field]: "" }));
      return;
    }

    setFormData(prev => {
      const nextData = { ...prev, [field]: value };
      if (nextData.fromDate && nextData.toDate) {
        const from = parseLocalDate(nextData.fromDate);
        const to = parseLocalDate(nextData.toDate);
        if (!from || !to) { nextData.noOfDays = 1; return nextData; }
        const daysDifference = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
        nextData.noOfDays = daysDifference > 0 ? daysDifference : 1;
        setFieldErrors(prevErrors => ({ ...prevErrors, toDate: "", noOfDays: "", fromDate: "" }));
      } else {
        nextData.noOfDays = 1;
      }
      return nextData;
    });
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0] || null;
    if (!f) { setFormData(prev => ({ ...prev, supportDoc: null })); return; }

    const ext = f.name.split(".").pop().toLowerCase();
    if (!BOOKING_ALLOWED_EXTS.includes(ext)) {
      setError("Only JPG, JPEG, and PNG files are allowed for the support document.");
      e.target.value = "";
      setFormData(prev => ({ ...prev, supportDoc: null }));
      return;
    }

    setError("");

    if (f.size <= BOOKING_MAX_FILE_BYTES) {
      setFormData(prev => ({ ...prev, supportDoc: f }));
      return;
    }

    try {
      const blob = await compressBookingImage(f, BOOKING_MAX_FILE_BYTES);
      const compressed = new File([blob], f.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
      setFormData(prev => ({ ...prev, supportDoc: compressed }));
    } catch {
      setError("Image is too large and could not be compressed. Please use a smaller file.");
      e.target.value = "";
      setFormData(prev => ({ ...prev, supportDoc: null }));
    }
  };

  const applyNoOfRooms = (rawVal) => {
    let val = parseInt(rawVal, 10);
    if (isNaN(val) || val < 1) val = 1;
    if (val > 10) val = 10;
    setFormData(prev => ({ ...prev, noOfRooms: val }));
    const count = val - 1;
    setAdditionalVisitors(prev => {
      const next = [...prev];
      while (next.length < count) next.push({ name: "", mobile: "", relationship: "Guest" });
      return next.slice(0, count);
    });
  };

  const handleNoOfRoomsChange = (e) => applyNoOfRooms(e.target.value);
  const adjustNoOfRooms = (delta) => applyNoOfRooms((parseInt(formData.noOfRooms, 10) || 1) + delta);

  const handleAdditionalVisitorChange = (idx, field, value) => {
    setAdditionalVisitors(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const resetForm = () => {
    setReferenceID("");
    setAdditionalVisitors([]);
    setFormData({
      visitorName: "",
      visitorMobile: "",
      visitorEmail: "",
      address: "",
      stateID: "",
      cityID: "",
      guestHouseID: "",
      roomType: "R",
      visitType: "P",
      purposeOfVisit: "",
      fromDate: "",
      toDate: "",
      noOfDays: 1,
      noOfRooms: 1,
      relationWithApplicant: "Guest",
      remarks: "",
      supportDoc: null,
      departmentID: "",
      employeeID: "",
      ghMemberID: "",
      teacherID: ""
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const nextErrors = {};
    if (!formData.visitorName.trim()) nextErrors.visitorName = "Visitor name(s) required";
    if (!/^\d{10}$/.test(formData.visitorMobile)) nextErrors.visitorMobile = "Mobile must be exactly 10 digits";
    if (!formData.guestHouseID) nextErrors.guestHouseID = "Guest House is required";
    if (!formData.purposeOfVisit) nextErrors.purposeOfVisit = "Purpose of Visit is required";
    if (!formData.fromDate) nextErrors.fromDate = "Check-In Date is required";
    if (!formData.toDate) nextErrors.toDate = "Check-Out Date is required";

    const noOfRoomsNum = parseInt(formData.noOfRooms, 10) || 1;
    additionalVisitors.forEach((v, idx) => {
      if (!v.name.trim()) nextErrors[`visitor${idx + 2}Name`] = `${getOrdinal(idx + 2)} visitor name is required`;
      if (!/^\d{10}$/.test(v.mobile.replace(/\D/g, ""))) nextErrors[`visitor${idx + 2}Mobile`] = `${getOrdinal(idx + 2)} visitor mobile must be 10 digits`;
    });

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});

    try {
      setLoading(true);

      const payload = new FormData();
      payload.append("moduleId", String(moduleId));
      payload.append("visitorName", formData.visitorName.trim());
      payload.append("visitorMobile", formData.visitorMobile);
      if (formData.visitorEmail) payload.append("visitorEmail", formData.visitorEmail);
      if (formData.address) payload.append("address", formData.address);
      if (formData.cityID) payload.append("cityID", formData.cityID);
      if (formData.stateID) payload.append("stateID", formData.stateID);
      payload.append("guestHouseID", formData.guestHouseID);
      payload.append("roomType", formData.roomType || "R");
      payload.append("visitType", formData.visitType);
      payload.append("purposeOfVisit", formData.purposeOfVisit);
      payload.append("fromDate", formData.fromDate);
      payload.append("toDate", formData.toDate);
      payload.append("relationWithApplicant", formData.relationWithApplicant);
      if (formData.remarks) payload.append("remarks", formData.remarks);
      if (formData.departmentID) payload.append("departmentID", formData.departmentID);
      if (formData.employeeID) payload.append("employeeID", formData.employeeID);
      if (formData.ghMemberID) payload.append("ghMemberID", formData.ghMemberID);
      if (formData.teacherID) payload.append("teacherID", formData.teacherID);
      if (formData.supportDoc) payload.append("supportDoc", formData.supportDoc);
      payload.append("noOfRooms", String(noOfRoomsNum));
      if (additionalVisitors.length > 0) {
        payload.append("visitors", JSON.stringify(additionalVisitors.map(v => ({
          name: v.name.trim(),
          mobile: v.mobile.replace(/\D/g, ""),
          relationship: v.relationship || "Guest"
        }))));
      }

      const res = await fetch(`${GH_API}/booking/create-admin`, {
        method: "POST",
        body: payload
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Failed to create booking");
        return;
      }

      const refs = data.bookingRefs && data.bookingRefs.length > 1
        ? ` Booking References: ${data.bookingRefs.join(", ")}.`
        : ` Booking ID: ${data.bookingID}.`;
      setSuccess(`${data.message || "Booking request submitted successfully."}${refs}`);
      resetForm();
    } catch (err) {
      setError("Error submitting booking request");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (moduleId !== 10) return null;

  return (
    <div style={{ textAlign: "left", marginTop: "10px" }}>
      <a
        href="/guesthouse/booking-request-by-mobile"
        className="btn btn-primary"
        style={{ display: "inline-block", padding: "10px 20px", textDecoration: "none" }}
      >
        Booking Request(By Mobile)
      </a>

      <a
        href="/guesthouse/change-request"
        className="btn btn-primary"
        style={{ display: "inline-block", padding: "10px 20px", textDecoration: "none" }}
      >
        Change Request
      </a>

      <h3 className="ctitle">Booking Request by Admin (Visitor's Detail)</h3>

      <div className="col-lg-10">
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form
          onSubmit={handleSubmit}
          className="guesthouse-booking-form"
          name="BookingFormAdmin"
          id="BookingFormAdmin"
          encType="multipart/form-data"
        >
          <div className="admin-form-row-contact">
            <div className="booking-control-item">
              <label className="form-label">Visitors Names (comma for names)</label>
              <input
                className="form-control form-control-sm"
                name="visitorName"
                type="text"
                placeholder="Names...*"
                value={formData.visitorName}
                onChange={handleInputChange}
                required
              />
              {fieldErrors.visitorName && <div className="field-error">{fieldErrors.visitorName}</div>}
            </div>

            <div className="booking-control-item">
              <label className="form-label">Registered Reference User</label>
              <select
                className="form-select form-select-sm"
                value={referenceID}
                onChange={handleReferenceChange}
              >
                <option value="" disabled>Select Name</option>
                {referenceUsers.map(r => (
                  <option key={r.ReferenceID} value={r.ReferenceID}>{r.ReferenceName}</option>
                ))}
              </select>
            </div>

            <div className="booking-control-item">
              <label className="form-label">Visitor Mobile (10 digit)</label>
              <input
                className="form-control form-control-sm"
                name="visitorMobile"
                type="text"
                placeholder="Mobile...*"
                value={formData.visitorMobile}
                onChange={(e) => setFormData(prev => ({ ...prev, visitorMobile: e.target.value.replace(/[^0-9]/g, "") }))}
                maxLength={10}
                required
              />
              {fieldErrors.visitorMobile && <div className="field-error">{fieldErrors.visitorMobile}</div>}
            </div>

            <div className="booking-control-item">
              <label className="form-label">Visitor's EMail (Optional)</label>
              <input
                className="form-control form-control-sm"
                name="visitorEmail"
                type="email"
                placeholder="EMail..."
                value={formData.visitorEmail}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="visitor-location-row">
            <div className="visitor-location-item visitor-location-address">
              <label className="form-label">Visitor's Address</label>
              <input
                className="form-control form-control-sm"
                name="address"
                type="text"
                placeholder="Permanent Address..."
                value={formData.address}
                onChange={handleInputChange}
              />
            </div>
            <div className="visitor-location-item visitor-location-state">
              <label className="form-label">Visitor's State</label>
              <select
                className="form-select form-select-sm"
                value={formData.stateID}
                onChange={(e) => setFormData(prev => ({ ...prev, stateID: e.target.value, cityID: "" }))}
              >
                <option value="">Select State</option>
                {states.map(state => (
                  <option key={state.StateID} value={state.StateID}>{state.State}</option>
                ))}
              </select>
            </div>
            <div className="visitor-location-item visitor-location-city">
              <label className="form-label">Visitor City</label>
              <select
                className="form-select form-select-sm"
                name="cityID"
                value={formData.cityID}
                onChange={handleInputChange}
              >
                <option value="">Select City</option>
                {cities.map(city => (
                  <option key={city.CityID} value={city.CityID}>{city.City}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="admin-form-booking-controls">
            <div className="booking-control-item booking-control-guesthouse">
              <label className="form-label">Select Guest House</label>
              <select
                className="form-select form-select-sm"
                name="guestHouseID"
                value={formData.guestHouseID}
                onChange={handleGuestHouseChange}
                required
              >
                <option value="" disabled>Select Guest House</option>
                {guesthouses.map(gh => (
                  <option key={gh.GuestHouseID} value={gh.GuestHouseID}>{gh.GuestHouse}</option>
                ))}
              </select>
              {fieldErrors.guestHouseID && <div className="field-error">{fieldErrors.guestHouseID}</div>}
            </div>

            <div className="booking-control-item">
              <label className="form-label">Facility Type</label>
              <select
                className="form-select form-select-sm"
                name="roomType"
                value={formData.roomType}
                onChange={handleInputChange}
              >
                {getRoomTypeOptions().map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="booking-control-item">
              <label className="form-label">Type of Visit</label>
              <select
                className="form-select form-select-sm"
                name="visitType"
                value={formData.visitType}
                onChange={handleInputChange}
              >
                <option value="O">Official</option>
                <option value="P">Personal</option>
              </select>
            </div>

            <div className="booking-control-item">
              <label className="form-label">Purpose of Visit</label>
              <select
                className="form-select form-select-sm"
                name="purposeOfVisit"
                value={formData.purposeOfVisit}
                onChange={handleInputChange}
                required
              >
                <option value="" disabled>Select Purpose</option>
                {PURPOSE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {fieldErrors.purposeOfVisit && <div className="field-error">{fieldErrors.purposeOfVisit}</div>}
            </div>

            <div className="booking-control-item">
              <label className="form-label">Relationship</label>
              <select
                className="form-select form-select-sm"
                name="relationWithApplicant"
                value={formData.relationWithApplicant}
                onChange={handleInputChange}
              >
                {RELATIONSHIP_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="booking-control-item booking-control-rooms">
              <label className="form-label">NoOfRooms</label>
              <div className="rooms-stepper">
                <button type="button" className="rooms-stepper-btn" onClick={() => adjustNoOfRooms(-1)} disabled={formData.noOfRooms <= 1} aria-label="Decrease rooms">−</button>
                <input
                  className="form-control form-control-sm rooms-stepper-input"
                  type="number"
                  inputMode="numeric"
                  name="noOfRooms"
                  value={formData.noOfRooms}
                  onChange={handleNoOfRoomsChange}
                  onFocus={e => e.target.select()}
                  min="1"
                  max="10"
                />
                <button type="button" className="rooms-stepper-btn" onClick={() => adjustNoOfRooms(1)} disabled={formData.noOfRooms >= 10} aria-label="Increase rooms">+</button>
              </div>
              <small className="booking-hint-text">Max 10 rooms</small>
            </div>
          </div>

          {additionalVisitors.length > 0 && (
            <>
              <div className="visitor-details-heading">Additional Visitor Details</div>
              <div className="visitor-details-card">
                {additionalVisitors.map((v, idx) => (
                  <div className="visitor-details-entry" key={idx}>
                    <div className="visitor-entry-title">{getOrdinal(idx + 2)} Room Visitor</div>
                    <div className="visitor-details-grid">
                      <div className="visitor-field-item">
                        <label className="form-label">Visitor Name</label>
                        <input
                          className="form-control form-control-sm"
                          type="text"
                          placeholder="Visitor Name*"
                          value={v.name}
                          onChange={e => handleAdditionalVisitorChange(idx, "name", e.target.value.replace(/[^A-Za-z0-9 ]/g, ""))}
                        />
                        {fieldErrors[`visitor${idx + 2}Name`] && <div className="field-error">{fieldErrors[`visitor${idx + 2}Name`]}</div>}
                      </div>
                      <div className="visitor-field-item">
                        <label className="form-label">Mobile</label>
                        <input
                          className="form-control form-control-sm"
                          type="text"
                          placeholder="Mobile*"
                          value={v.mobile}
                          onChange={e => handleAdditionalVisitorChange(idx, "mobile", e.target.value.replace(/[^0-9]/g, ""))}
                          maxLength={10}
                        />
                        {fieldErrors[`visitor${idx + 2}Mobile`] && <div className="field-error">{fieldErrors[`visitor${idx + 2}Mobile`]}</div>}
                      </div>
                      <div className="visitor-field-item">
                        <label className="form-label">Relationship</label>
                        <select
                          className="form-select form-select-sm"
                          value={v.relationship}
                          onChange={e => handleAdditionalVisitorChange(idx, "relationship", e.target.value)}
                        >
                          {RELATIONSHIP_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="admin-dates-remarks-row">
            <div className="visitor-extra-item">
              <label className="form-label">CheckIn Date</label>
              <input
                className="form-control form-control-sm"
                type="date"
                value={formData.fromDate}
                onChange={(e) => handleDateChange("fromDate", e.target.value)}
                required
              />
              {fieldErrors.fromDate && <div className="field-error">{fieldErrors.fromDate}</div>}
            </div>

            <div className="visitor-extra-item">
              <label className="form-label">CheckOut Date</label>
              <input
                className="form-control form-control-sm"
                type="date"
                value={formData.toDate}
                onChange={(e) => handleDateChange("toDate", e.target.value)}
                required
              />
              {fieldErrors.toDate && <div className="field-error">{fieldErrors.toDate}</div>}
            </div>

            <div className="visitor-extra-item">
              <label className="form-label">Days</label>
              <input
                className="form-control form-control-sm"
                type="number"
                value={formData.noOfDays}
                min="1"
                readOnly
              />
              {fieldErrors.noOfDays && <div className="field-error">{fieldErrors.noOfDays}</div>}
            </div>

            <div className="visitor-extra-item">
              <label className="form-label">Remarks</label>
              <input
                className="form-control form-control-sm"
                name="remarks"
                type="text"
                placeholder="Remarks..."
                value={formData.remarks}
                onChange={handleInputChange}
              />
            </div>

            <div className="visitor-extra-item">
              <label className="form-label">
                Invitation/sponsorship&nbsp;
                <i
                  className="bi bi-info-circle text-primary"
                  title="Allowed: JPG, JPEG, PNG only. Max size 200 KB. Larger images will be auto-compressed."
                  style={{ cursor: "help" }}
                ></i>
              </label>
              <input
                type="file"
                name="supportDoc"
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png"
              />
              <small className="text-muted">JPG/JPEG/PNG only, max 200KB (auto-compress)</small>
            </div>
          </div>

          <div className="col-sm-12">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
